import React from 'react';
import { Form } from 'react-bootstrap';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import FieldError from './FieldError';

/**
 * Password input with a show/hide toggle and an inline error line, shared by the
 * sign-in/register form and the reset-password page.
 *
 * - `error`: message shown under the field (also marks it invalid).
 * - `invalid`: highlight the field without a message of its own, e.g. the Password
 *   field when only Confirm Password carries the "do not match" text.
 * - `describedBy`: id of the message that explains an `invalid` field with no `error`.
 */
const PasswordField = ({
  id,
  label,
  value,
  onChange,
  show,
  onToggleShow,
  error,
  invalid = Boolean(error),
  describedBy,
  requiredMark = true,
}) => {
  const errorId = `${id}-error`;
  // Named after its own field ("Show confirm password"), so the two toggles on a form are
  // distinguishable to a screen reader and to anyone who hovers them.
  const toggleLabel = `${show ? 'Hide' : 'Show'} ${String(label).toLowerCase()}`;
  return (
    <Form.Group className="mb-3" controlId={id}>
      <Form.Label className="form-label">
        {label}
        {requiredMark && <span className="required-mark" aria-hidden="true">*</span>}
      </Form.Label>
      <div className="password-input-wrap">
        <Form.Control
          type={show ? 'text' : 'password'}
          value={value}
          onChange={onChange}
          required
          className="form-input password-input"
          aria-invalid={invalid || undefined}
          aria-describedby={error ? errorId : describedBy}
        />
        <button
          type="button"
          className="password-toggle-btn"
          onClick={onToggleShow}
          aria-label={toggleLabel}
          title={toggleLabel}
        >
          {show ? <FaEyeSlash size={18} aria-hidden /> : <FaEye size={18} aria-hidden />}
        </button>
      </div>
      <FieldError id={errorId}>{error}</FieldError>
    </Form.Group>
  );
};

export default PasswordField;
